import { IAmazonMvl } from "../models/mvl/IAmazonMvl";
import { IVehicle } from "../models/Vehicle";

// Scores MVL rows against the vehicle on file so the closest KType can be suggested.
// Purely client side - it never changes what the search asks the API for.

export type SignalKey = "power" | "fuel" | "engine" | "body" | "drive" | "years" | "variant";
export type SignalState = "match" | "near" | "mismatch" | "unknown";

export interface IKTypeMatch {
  score: number;
  max: number;
  percent: number | null;
  signals: Record<SignalKey, SignalState>;
  matched: string[];
  mismatched: string[];
  // The build window is a hard gate - a row outside it cannot be the vehicle, whatever else matches.
  outOfRange: boolean;
  outOfRangeReason: string | null;
}

export interface IScoredMvl {
  row: IAmazonMvl;
  match: IKTypeMatch;
}

const WEIGHTS: Record<SignalKey, number> = {
  power: 30,
  fuel: 25,
  engine: 20,
  body: 15,
  years: 15,
  drive: 10,
  variant: 10,
};

// A car is registered after it is built, so when we only have the registration date we allow it to
// fall this far past the end of production. Build dates are gated with no tolerance at all.
const REGISTRATION_LAG_MS = 1000 * 60 * 60 * 24 * 365;

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

function parseMonthYear(value: string | null | undefined): Date | null {
  const match = (value ?? "").trim().match(/^([A-Za-z]{3})[a-z]*\s+(\d{4})$/);
  if (!match) return null;
  const month = MONTHS[match[1].toLowerCase()];
  if (month === undefined) return null;
  return new Date(Number(match[2]), month, 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

function parseNumber(value: string | number | null | undefined): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number.parseFloat((value ?? "").toString().replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

// MVL names carry diacritics the DVLA data does not ("Scénic", "Škoda"), so strip them before
// comparing anything - the API search is accent insensitive for the same reason.
function deaccent(value: string | null | undefined): string {
  return (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function normaliseFuel(value: string | null | undefined): string | null {
  const v = deaccent(value).toLowerCase();
  if (!v.trim()) return null;
  if (v.includes("hybrid")) return "hybrid";
  if (v.includes("diesel") || v.includes("heavy oil")) return "diesel";
  if (v.includes("petrol") || v.includes("gasoline")) return "petrol";
  if (v.includes("electric")) return "electric";
  return v.trim();
}

function normaliseBody(value: string | null | undefined): string | null {
  const v = deaccent(value).toLowerCase().replace(/[^a-z]/g, "");
  if (!v) return null;
  if (v.includes("estate") || v.includes("tourer") || v.includes("avant")) return "estate";
  if (v.includes("hatch")) return "hatchback";
  if (v.includes("saloon") || v.includes("sedan")) return "saloon";
  if (v.includes("coupe")) return "coupe";
  if (v.includes("convertible") || v.includes("cabrio")) return "convertible";
  if (v.includes("mpv")) return "mpv";
  if (v.includes("suv") || v.includes("offroad")) return "suv";
  return v;
}

function normaliseDrive(value: string | null | undefined): string | null {
  const v = deaccent(value).toLowerCase();
  if (!v.trim()) return null;
  if (v.includes("all") || v.includes("awd") || v.includes("4x4") || v.includes("four")) return "awd";
  if (v.includes("front")) return "front";
  if (v.includes("rear")) return "rear";
  return null;
}

// "2.0 D" -> 2, "1.6 D" -> 1.6, "D4" / "T5 AWD" -> null
function parseVariantLitres(variant: string | null | undefined): number | null {
  const match = (variant ?? "").match(/(\d\.\d)/);
  return match ? Number(match[1]) : null;
}

function tokenise(value: string | null | undefined): string[] {
  return deaccent(value)
    .toLowerCase()
    .split(/[^a-z0-9.]+/)
    .filter(Boolean);
}

interface IVehicleMatchContext {
  kw: number | null;
  hp: number | null;
  fuel: string | null;
  litres: number | null;
  body: string | null;
  drive: string | null;
  /** Build date if we have one, otherwise first registration - see isRegistrationDate. */
  built: Date | null;
  isRegistrationDate: boolean;
  builtLabel: string | null;
  year: number | null;
  descriptionTokens: string[];
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function monthYearLabel(date: Date): string {
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

export function buildVehicleMatchContext(vehicle: IVehicle): IVehicleMatchContext {
  const capacity = vehicle.combinedEngineCapacity ?? vehicle.engineCapacity ?? null;
  const buildDate = toDate(vehicle.manufacturerBuildDate);
  const registered = toDate(vehicle.dateFirstRegistered) ?? toDate(vehicle.dateOfFirstRegistrationUK);
  const built = buildDate ?? registered;

  return {
    kw: parseNumber(vehicle.maximumPowerInKW),
    hp: parseNumber(vehicle.maximumPowerBHP),
    fuel: normaliseFuel(vehicle.combinedFuelType || vehicle.fuelType || vehicle.fuelDescription),
    litres: capacity ? Math.round(capacity / 100) / 10 : null,
    body: normaliseBody(vehicle.bodyStyleDescription || vehicle.bodyStyle),
    drive: normaliseDrive(vehicle.driveType) ?? normaliseDrive(vehicle.driveAxle),
    built,
    isRegistrationDate: !buildDate && !!registered,
    builtLabel: built ? monthYearLabel(built) : vehicle.dvlaYearOfManufacture?.toString() ?? null,
    year: vehicle.dvlaYearOfManufacture ?? built?.getFullYear() ?? null,
    descriptionTokens: [
      ...tokenise(vehicle.modelVariantDescription),
      ...tokenise(vehicle.dvlaModel),
      ...tokenise(vehicle.combinedModel),
      ...tokenise(vehicle.dvlaVehicleDescription),
    ],
  };
}

export function scoreMvlRow(row: IAmazonMvl, ctx: IVehicleMatchContext): IKTypeMatch {
  const signals: Record<SignalKey, SignalState> = {
    power: "unknown",
    fuel: "unknown",
    engine: "unknown",
    body: "unknown",
    drive: "unknown",
    years: "unknown",
    variant: "unknown",
  };
  const matched: string[] = [];
  const mismatched: string[] = [];
  let score = 0;
  let max = 0;

  const award = (key: SignalKey, state: SignalState, weight: number, label: string) => {
    max += WEIGHTS[key];
    score += weight;
    signals[key] = state;
    if (state === "mismatch") mismatched.push(label);
    else matched.push(label);
  };

  // Power - the strongest discriminator between variants sharing a model.
  const rowKw = parseNumber(row.engineOutputKw);
  const rowHp = parseNumber(row.engineOutputHp);
  if (ctx.kw != null && rowKw != null) {
    const diff = Math.abs(ctx.kw - rowKw);
    const label = `${rowKw}kW vs ${Math.round(ctx.kw)}kW on file`;
    if (diff <= 2) award("power", "match", WEIGHTS.power, label);
    else if (diff <= 6) award("power", "near", WEIGHTS.power * 0.6, label);
    else award("power", "mismatch", 0, label);
  } else if (ctx.hp != null && rowHp != null) {
    const diff = Math.abs(ctx.hp - rowHp);
    const label = `${rowHp}hp vs ${Math.round(ctx.hp)}hp on file`;
    if (diff <= 3) award("power", "match", WEIGHTS.power, label);
    else if (diff <= 8) award("power", "near", WEIGHTS.power * 0.6, label);
    else award("power", "mismatch", 0, label);
  }

  const rowFuel = normaliseFuel(row.energy);
  if (ctx.fuel && rowFuel) {
    const isMatch = rowFuel === ctx.fuel || rowFuel.includes(ctx.fuel) || ctx.fuel.includes(rowFuel);
    award("fuel", isMatch ? "match" : "mismatch", isMatch ? WEIGHTS.fuel : 0, `Fuel ${row.energy}`);
  }

  const rowLitres = parseVariantLitres(row.variantName);
  if (ctx.litres != null && rowLitres != null) {
    const diff = Math.abs(ctx.litres - rowLitres);
    const label = `${rowLitres.toFixed(1)}L vs ${ctx.litres.toFixed(1)}L on file`;
    if (diff <= 0.05) award("engine", "match", WEIGHTS.engine, label);
    else if (diff <= 0.15) award("engine", "near", WEIGHTS.engine * 0.5, label);
    else award("engine", "mismatch", 0, label);
  }

  const rowBody = normaliseBody(row.bodyStyle);
  if (ctx.body && rowBody) {
    const isMatch = rowBody === ctx.body;
    award("body", isMatch ? "match" : "mismatch", isMatch ? WEIGHTS.body : 0, `Body ${row.bodyStyle}`);
  }

  const rowDrive = normaliseDrive(row.driveType);
  if (ctx.drive && rowDrive) {
    const isMatch = rowDrive === ctx.drive;
    award("drive", isMatch ? "match" : "mismatch", isMatch ? WEIGHTS.drive : 0, `Drive ${row.driveType}`);
  }

  // Build window - a hard gate. Anything built outside it is a different car, however well the
  // rest of the row lines up, so it is ruled out rather than just marked down.
  let outOfRange = false;
  let outOfRangeReason: string | null = null;

  const start = parseMonthYear(row.productStartMonthYear);
  const end = parseMonthYear(row.productEndMonthYear);
  const window = [row.productStartMonthYear, row.productEndMonthYear].filter(Boolean).join(" – ");

  if ((start || end) && (ctx.built || ctx.year != null)) {
    max += WEIGHTS.years;

    let inRange: boolean;
    let lagged = false;

    if (ctx.built) {
      const time = ctx.built.getTime();
      // Registration can trail the end of production; an actual build date gets no tolerance.
      const lag = ctx.isRegistrationDate ? REGISTRATION_LAG_MS : 0;
      const afterStart = !start || time >= start.getTime();
      // "Dec 2006" parses to the 1st, but the window covers the whole month.
      const beforeEnd = !end || time <= endOfMonth(end).getTime() + lag;
      inRange = afterStart && beforeEnd;
      lagged = inRange && !!end && time > endOfMonth(end).getTime();
    } else {
      const year = ctx.year as number;
      inRange = (!start || year >= start.getFullYear()) && (!end || year <= end.getFullYear());
    }

    if (inRange) {
      score += lagged ? WEIGHTS.years * 0.75 : WEIGHTS.years;
      signals.years = lagged ? "near" : "match";
      matched.push(`Built ${window}`);
    } else {
      signals.years = "mismatch";
      outOfRange = true;
      outOfRangeReason = `Built ${window}, vehicle is ${ctx.builtLabel ?? "outside that window"}`;
      mismatched.push(outOfRangeReason);
    }
  }

  // Only credit the variant name when it literally appears on the vehicle record (e.g. "D5", "T5").
  const variantToken = deaccent(row.variantName).toLowerCase().replace(/[^a-z0-9]/g, "");
  if (variantToken && ctx.descriptionTokens.length > 0) {
    const isMatch = ctx.descriptionTokens.some(
      (token) => token.replace(/[^a-z0-9]/g, "") === variantToken
    );
    if (isMatch) award("variant", "match", WEIGHTS.variant, `Variant "${row.variantName}" on V5`);
  }

  return {
    score,
    max,
    percent: max > 0 ? Math.round((score / max) * 100) : null,
    signals,
    matched,
    mismatched,
    outOfRange,
    outOfRangeReason,
  };
}

export function scoreMvlRows(rows: IAmazonMvl[], vehicle: IVehicle | null | undefined): IScoredMvl[] {
  if (!vehicle) return rows.map((row) => ({ row, match: emptyMatch() }));

  const ctx = buildVehicleMatchContext(vehicle);
  const scored = rows.map((row) => ({ row, match: scoreMvlRow(row, ctx) }));

  // Ranking is display only - the search results themselves are untouched. Rows outside the build
  // window always sink to the bottom, however well they score on everything else.
  return scored.sort((a, b) => {
    if (a.match.outOfRange !== b.match.outOfRange) return a.match.outOfRange ? 1 : -1;
    return (b.match.percent ?? -1) - (a.match.percent ?? -1);
  });
}

function emptyMatch(): IKTypeMatch {
  return {
    score: 0,
    max: 0,
    percent: null,
    signals: {
      power: "unknown",
      fuel: "unknown",
      engine: "unknown",
      body: "unknown",
      drive: "unknown",
      years: "unknown",
      variant: "unknown",
    },
    matched: [],
    mismatched: [],
    outOfRange: false,
    outOfRangeReason: null,
  };
}
